/**
 * M007-F010: Parse RRULE string into human-readable format
 * Examples:
 *   "RRULE:FREQ=WEEKLY;BYDAY=MO,WE,FR" -> "Weekly on Monday, Wednesday, Friday"
 *   "RRULE:FREQ=DAILY;COUNT=5" -> "Daily, 5 times"
 *   "RRULE:FREQ=MONTHLY;BYMONTHDAY=15" -> "Monthly on the 15th"
 */
export function parseRRuleToHumanReadable(rrule) {
  if (!rrule) return '';
  
  // Remove RRULE: prefix if present
  const rule = rrule.replace(/^RRULE:/i, '');
  
  // Parse rule into key-value pairs
  const parts = {};
  rule.split(';').forEach(part => {
    const [key, value] = part.split('=');
    if (key && value) {
      parts[key.toUpperCase()] = value;
    }
  });
  
  const dayNames = {
    'SU': 'Sunday', 'MO': 'Monday', 'TU': 'Tuesday', 'WE': 'Wednesday',
    'TH': 'Thursday', 'FR': 'Friday', 'SA': 'Saturday'
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  let result = '';
  
  // Frequency with interval
  const interval = parseInt(parts.INTERVAL || '1', 10);
  const freq = parts.FREQ?.toUpperCase();
  
  switch (freq) {
    case 'DAILY':
      result = interval === 1 ? 'Daily' : `Every ${interval} days`;
      break;
    case 'WEEKLY':
      result = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
      break;
    case 'MONTHLY':
      result = interval === 1 ? 'Monthly' : `Every ${interval} months`;
      break;
    case 'YEARLY':
      result = interval === 1 ? 'Yearly' : `Every ${interval} years`;
      break;
    default:
      return rrule; // Return original if unknown frequency
  }
  
  // Days of week (BYDAY)
  if (parts.BYDAY) {
    const days = parts.BYDAY.split(',').map(d => {
      // Handle ordinal prefixes like "1MO" (first Monday), "-1FR" (last Friday)
      const match = d.match(/^(-?\d)?([A-Z]{2})$/);
      if (match) {
        const ordinal = match[1];
        const day = dayNames[match[2]] || match[2];
        if (ordinal === '1') return `first ${day}`;
        if (ordinal === '2') return `second ${day}`;
        if (ordinal === '3') return `third ${day}`;
        if (ordinal === '4') return `fourth ${day}`;
        if (ordinal === '-1') return `last ${day}`;
        return day;
      }
      return dayNames[d] || d;
    });
    result += ` on ${days.join(', ')}`;
  }
  
  // Day of month (BYMONTHDAY)
  if (parts.BYMONTHDAY) {
    const days = parts.BYMONTHDAY.split(',');
    const suffix = (n) => {
      if (n >= 11 && n <= 13) return 'th';
      switch (n % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const dayStr = days.map(d => {
      const num = parseInt(d, 10);
      return num === -1 ? 'last day' : `${num}${suffix(num)}`;
    }).join(', ');
    result += ` on the ${dayStr}`;
  }
  
  // Month (BYMONTH)
  if (parts.BYMONTH) {
    const months = parts.BYMONTH.split(',').map(m => monthNames[parseInt(m, 10) - 1] || m);
    result += ` in ${months.join(', ')}`;
  }
  
  // Count
  if (parts.COUNT) {
    result += `, ${parts.COUNT} times`;
  }
  
  // Until date
  if (parts.UNTIL) {
    const until = parts.UNTIL;
    // Parse YYYYMMDD or YYYYMMDDTHHMMSSZ format
    const year = until.substring(0, 4);
    const month = monthNames[parseInt(until.substring(4, 6), 10) - 1];
    const day = parseInt(until.substring(6, 8), 10);
    result += `, until ${month} ${day}, ${year}`;
  }
  
  return result;
}

/**
 * Get a human-readable label for a booking's recurrence pattern
 */
export function getRecurrenceLabel(booking) {
  if (!booking) return '';
  
  // For master recurring events, parse the RRULE
  if (booking.booking_type === 'recurring' && booking.google_recurrence_rule) {
    return parseRRuleToHumanReadable(booking.google_recurrence_rule);
  }
  
  // For instances of recurring events
  if (booking.booking_type === 'recurring_instance') {
    return 'Part of a recurring series';
  }
  
  // Legacy: check recurring object or default
  if (booking.recurring?.recurrencePattern) {
    return booking.recurring.recurrencePattern;
  }

  return '';
}

const RRULE_DAY_TO_JS = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

function parseRRuleParts(rrule) {
  const rule = rrule.replace(/^RRULE:/i, '');
  const parts = {};
  rule.split(';').forEach((part) => {
    const [key, value] = part.split('=');
    if (key && value) {
      parts[key.toUpperCase()] = value;
    }
  });
  return parts;
}

// UNTIL is either YYYYMMDD or YYYYMMDDTHHMMSSZ (RFC5545). Treat a bare date
// as inclusive of the whole day so an occurrence landing exactly on it counts.
function parseRRuleUntil(until) {
  if (!until) return null;
  const year = parseInt(until.substring(0, 4), 10);
  const month = parseInt(until.substring(4, 6), 10) - 1;
  const day = parseInt(until.substring(6, 8), 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  if (until.length > 8 && until.includes('T')) {
    const hh = parseInt(until.substring(9, 11), 10) || 0;
    const mm = parseInt(until.substring(11, 13), 10) || 0;
    const ss = parseInt(until.substring(13, 15), 10) || 0;
    return new Date(Date.UTC(year, month, day, hh, mm, ss));
  }
  return new Date(Date.UTC(year, month, day, 23, 59, 59));
}

// Handles DAILY / MONTHLY / YEARLY. WEEKLY is handled separately below by
// weeklyByDayNumbers/occurrenceOnWeekday, which is BYDAY-aware — a plain
// "anchor + n weeks" step (this function's old WEEKLY case) silently ignores
// every BYDAY value but the anchor's own weekday.
function addPeriodsFromAnchor(anchorStart, freq, interval, periods) {
  const d = new Date(anchorStart);
  const total = interval * periods;
  switch (freq) {
    case 'DAILY':
      d.setDate(d.getDate() + total);
      return d;
    case 'MONTHLY':
      d.setMonth(d.getMonth() + total);
      return d;
    case 'YEARLY':
      d.setFullYear(d.getFullYear() + total);
      return d;
    default:
      return null;
  }
}

// BYDAY values as JS Date#getDay() numbers (0=Sunday..6=Saturday), sorted
// and de-duplicated. Ordinal prefixes (1MO, -1FR — "first/last day of the
// month") are stripped: they are a MONTHLY-only construct and this app only
// ever generates/consumes WEEKLY BYDAY, so the bare weekday is all that's
// meaningful here. Falls back to the anchor's own weekday when BYDAY is
// absent, preserving the single-day behaviour exactly.
function weeklyByDayNumbers(bydayValue, anchorStart) {
  if (!bydayValue) return [anchorStart.getDay()];
  const days = Array.from(new Set(
    bydayValue.split(',')
      .map((d) => d.replace(/^-?\d+/, ''))
      .map((d) => RRULE_DAY_TO_JS[d])
      .filter((d) => d !== undefined),
  ));
  return days.length ? days.sort((a, b) => a - b) : [anchorStart.getDay()];
}

// Local-time Sunday 00:00:00 boundary containing `date`. Local time
// (setDate/getHours/etc, not UTC) matches the rest of this module's date
// arithmetic and what a browser-side RRULE expansion naturally does — see
// the DST caveat in getNextOccurrence's doc comment below.
function weekStartLocal(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

// A candidate occurrence on `jsDay` of the week starting `weekStart`, at the
// same local time-of-day as `timeOfDaySource` (the anchor).
function occurrenceOnWeekday(weekStart, jsDay, timeOfDaySource) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + jsDay);
  d.setHours(
    timeOfDaySource.getHours(),
    timeOfDaySource.getMinutes(),
    timeOfDaySource.getSeconds(),
    timeOfDaySource.getMilliseconds(),
  );
  return d;
}

const SUPPORTED_FREQS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
// Safety cap so a malformed/unbounded rule can never loop forever.
const MAX_OCCURRENCE_ITERATIONS = 3000;

/**
 * M004-F0074 / Defect A: Resolve the occurrence of a recurring series that
 * should actually be displayed to the user, instead of the series' anchor
 * (DTSTART) date.
 *
 * Given the RRULE and the anchor booking's own start/end, walk the series
 * forward from the anchor and return:
 *  - the first occurrence on/after `now` (the "next" occurrence), or
 *  - if the series has already ended (COUNT/UNTIL exhausted before `now`,
 *    or the iteration cap was hit), the LAST occurrence in the series,
 *    explicitly marked `isEnded: true` so callers never present a past
 *    series as upcoming.
 *
 * Reuses `parseRRuleToHumanReadable`'s own RRULE field parsing (FREQ,
 * INTERVAL, COUNT, UNTIL) rather than introducing a second parser. WEEKLY
 * honours every value in a multi-day BYDAY (e.g. `BYDAY=MO,WE,FR`) — the
 * earliest future occurrence is chosen across all listed days, in the same
 * chronological (and COUNT-index) order RFC5545 defines — not just the
 * anchor's own weekday.
 *
 * DST caveat: occurrence arithmetic uses local-time Date methods
 * (setDate/setHours, matching the rest of this module), so a series' clock
 * time is stable in the *local* civil calendar, not in UTC. That is correct
 * for a UK studio's browser session (Europe/London), which is this app's
 * only real runtime, but is not asserted here — no test in this repo
 * exercises a series occurrence crossing a UK clock change (last Sunday in
 * March/October, e.g. 25 Oct 2026).
 */
export function getNextOccurrence(rrule, anchorStartISO, anchorEndISO, now = new Date()) {
  const anchorStart = new Date(anchorStartISO);
  const anchorEndDate = anchorEndISO ? new Date(anchorEndISO) : null;
  const durationMs = anchorEndDate && !Number.isNaN(anchorEndDate.getTime())
    ? anchorEndDate.getTime() - anchorStart.getTime()
    : null;

  const buildResult = (start, isEnded, isSeries) => ({
    start,
    end: durationMs !== null ? new Date(start.getTime() + durationMs) : anchorEndDate,
    isEnded,
    isSeries,
  });

  const fallbackToAnchor = () => {
    const hasEnded = anchorEndDate && !Number.isNaN(anchorEndDate.getTime())
      ? anchorEndDate.getTime() < now.getTime()
      : anchorStart.getTime() < now.getTime();
    return buildResult(anchorStart, hasEnded, false);
  };

  if (!rrule || Number.isNaN(anchorStart.getTime())) {
    return fallbackToAnchor();
  }

  const parts = parseRRuleParts(rrule);
  const freq = parts.FREQ?.toUpperCase();
  if (!SUPPORTED_FREQS.includes(freq)) {
    return fallbackToAnchor();
  }

  const interval = Math.max(1, parseInt(parts.INTERVAL || '1', 10));
  const count = parts.COUNT ? parseInt(parts.COUNT, 10) : null;
  const until = parseRRuleUntil(parts.UNTIL);

  if (freq === 'WEEKLY') {
    const dayNumbers = weeklyByDayNumbers(parts.BYDAY, anchorStart);
    const anchorWeekStart = weekStartLocal(anchorStart);

    let lastValid = anchorStart;
    let idx = 0;

    for (let block = 0; block < MAX_OCCURRENCE_ITERATIONS; block++) {
      const blockWeekStart = new Date(anchorWeekStart);
      blockWeekStart.setDate(blockWeekStart.getDate() + block * interval * 7);

      for (const jsDay of dayNumbers) {
        const candidate = occurrenceOnWeekday(blockWeekStart, jsDay, anchorStart);
        // A series never yields anything before its own DTSTART, even if
        // BYDAY includes a day earlier in the anchor's own week.
        if (candidate.getTime() < anchorStart.getTime()) continue;

        if (count !== null && idx >= count) {
          return buildResult(lastValid, true, true);
        }
        if (until && candidate.getTime() > until.getTime()) {
          return buildResult(lastValid, true, true);
        }

        lastValid = candidate;
        idx += 1;

        if (candidate.getTime() >= now.getTime()) {
          return buildResult(candidate, false, true);
        }
      }

      // Independent safety valve: bail once enough individual occurrences
      // have been examined, regardless of how many BYDAY values there are.
      if (idx >= MAX_OCCURRENCE_ITERATIONS) break;
    }

    return buildResult(lastValid, true, true);
  }

  // DAILY / MONTHLY / YEARLY: step directly from the anchor by whole periods.
  let lastValid = anchorStart;

  for (let n = 0; n < MAX_OCCURRENCE_ITERATIONS; n++) {
    const withinCount = count === null || n < count;
    if (!withinCount) {
      return buildResult(lastValid, true, true);
    }

    const candidate = n === 0 ? anchorStart : addPeriodsFromAnchor(anchorStart, freq, interval, n);
    if (!candidate) {
      return buildResult(lastValid, lastValid.getTime() < now.getTime(), true);
    }

    if (until && candidate.getTime() > until.getTime()) {
      return buildResult(lastValid, true, true);
    }

    lastValid = candidate;

    if (candidate.getTime() >= now.getTime()) {
      return buildResult(candidate, false, true);
    }
  }

  // Iteration cap hit without reaching `now`: treat as ended at the last
  // occurrence we computed rather than guessing further into the future.
  return buildResult(lastValid, true, true);
}

// M009-F014 / T-WEB-EVS-014: "My Bookings" needs one card per series
// (owner, verbatim, 2026-09-07: "this need to reworked to make it
// understandable" — a series shown as one arbitrary instance date hides
// when it starts and ends). getNextOccurrence above answers "which ONE
// date do I show"; this answers "what is the WHOLE list of dates", for the
// series card's from/until summary and its expandable per-date list.
//
// Walks the same RRULE arithmetic as getNextOccurrence (shares every helper
// — weeklyByDayNumbers, weekStartLocal, occurrenceOnWeekday,
// addPeriodsFromAnchor, parseRRuleParts, parseRRuleUntil — no second
// parser), but collects every occurrence instead of stopping at the first
// one on/after `now`.
//
// `cap` (default 104 — two years of a weekly series) is a safety bound for
// a genuinely open-ended series (no COUNT, no UNTIL) only. A bounded series
// (COUNT and/or UNTIL) is never cut short by it as long as its real
// occurrence count is under the cap; `isTruncated` is true only when the
// cap itself, not the rule, is why the list stops — so a caller can say
// "…and more" honestly instead of implying the series ends there.
export function expandOccurrences(rrule, anchorStartISO, anchorEndISO, { cap = 104 } = {}) {
  const anchorStart = new Date(anchorStartISO);
  const anchorEndDate = anchorEndISO ? new Date(anchorEndISO) : null;
  const durationMs = anchorEndDate && !Number.isNaN(anchorEndDate.getTime())
    ? anchorEndDate.getTime() - anchorStart.getTime()
    : null;

  const toOccurrence = (start) => ({
    start,
    end: durationMs !== null ? new Date(start.getTime() + durationMs) : anchorEndDate,
  });

  if (Number.isNaN(anchorStart.getTime())) {
    return { occurrences: [], isOpenEnded: false, isTruncated: false, untilDate: null };
  }

  if (!rrule) {
    return { occurrences: [toOccurrence(anchorStart)], isOpenEnded: false, isTruncated: false, untilDate: null };
  }

  const parts = parseRRuleParts(rrule);
  const freq = parts.FREQ?.toUpperCase();
  if (!SUPPORTED_FREQS.includes(freq)) {
    return { occurrences: [toOccurrence(anchorStart)], isOpenEnded: false, isTruncated: false, untilDate: null };
  }

  const interval = Math.max(1, parseInt(parts.INTERVAL || '1', 10));
  const count = parts.COUNT ? parseInt(parts.COUNT, 10) : null;
  const until = parseRRuleUntil(parts.UNTIL);
  const isOpenEnded = count === null && until === null;

  const occurrences = [];
  let isTruncated = false;

  if (freq === 'WEEKLY') {
    const dayNumbers = weeklyByDayNumbers(parts.BYDAY, anchorStart);
    const anchorWeekStart = weekStartLocal(anchorStart);
    let idx = 0;

    blockLoop:
    for (let block = 0; block < MAX_OCCURRENCE_ITERATIONS; block++) {
      const blockWeekStart = new Date(anchorWeekStart);
      blockWeekStart.setDate(blockWeekStart.getDate() + block * interval * 7);

      for (const jsDay of dayNumbers) {
        const candidate = occurrenceOnWeekday(blockWeekStart, jsDay, anchorStart);
        // A series never yields anything before its own DTSTART, even if
        // BYDAY includes a day earlier in the anchor's own week.
        if (candidate.getTime() < anchorStart.getTime()) continue;
        if (count !== null && idx >= count) break blockLoop;
        if (until && candidate.getTime() > until.getTime()) break blockLoop;

        occurrences.push(toOccurrence(candidate));
        idx += 1;

        if (occurrences.length >= cap) {
          isTruncated = isOpenEnded || (count !== null && idx < count) || (until !== null);
          break blockLoop;
        }
      }
    }
  } else {
    // DAILY / MONTHLY / YEARLY: step directly from the anchor by whole periods.
    for (let n = 0; n < MAX_OCCURRENCE_ITERATIONS; n++) {
      if (count !== null && n >= count) break;

      const candidate = n === 0 ? anchorStart : addPeriodsFromAnchor(anchorStart, freq, interval, n);
      if (!candidate) break;
      if (until && candidate.getTime() > until.getTime()) break;

      occurrences.push(toOccurrence(candidate));

      if (occurrences.length >= cap) {
        isTruncated = isOpenEnded || (count !== null && n + 1 < count) || (until !== null);
        break;
      }
    }
  }

  return { occurrences, isOpenEnded, isTruncated, untilDate: until };
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// M009-F014: "Every Tuesday" / "Every Monday, Wednesday" style pattern
// label for a series card, aligned with the Term Planner's "weekly slot"
// mental model rather than the raw RRULE. WEEKLY is the only pattern this
// app generates or consumes (see the DST-caveat comment on
// getNextOccurrence above), so that is the only shape given a dedicated
// "Every <day>" phrasing; anything else falls back to the existing
// parseRRuleToHumanReadable label rather than guessing a new phrasing for
// a shape no fixture or production booking has ever produced.
export function describeSeriesPattern(rrule, anchorStartISO) {
  if (!rrule) return '';
  const parts = parseRRuleParts(rrule);
  const freq = parts.FREQ?.toUpperCase();
  if (freq !== 'WEEKLY') {
    return parseRRuleToHumanReadable(rrule);
  }
  const anchorStart = new Date(anchorStartISO);
  const dayNumbers = weeklyByDayNumbers(parts.BYDAY, anchorStart);
  const names = dayNumbers.map((d) => WEEKDAY_NAMES[d]);
  const interval = Math.max(1, parseInt(parts.INTERVAL || '1', 10));
  const every = interval === 1 ? 'Every' : `Every ${interval} weeks on`;
  return `${every} ${names.join(', ')}`;
}
